/**
 * One stored `Payment.method` string, turned into something a person reads.
 *
 * ONE function and not two, which is the whole reason it is a file. The
 * payments screen used to answer this question twice and differently: the
 * filter above the table translated a built-in token, and the rows underneath
 * printed the raw string. So the dropdown offered "Bank transfer" and every row
 * it selected said `pangaülekanne` - the same method under two names, on one
 * screen, in every interface language including Estonian.
 *
 * Three kinds of stored value and they are not interchangeable:
 *   - a BUILT-IN token (`pangaülekanne`, `stripe`) has a translated label;
 *   - a SYSTEM token (`manual`) has one too, and never appears in settings;
 *   - anything else is a custom row, an imported Perfex name or a word somebody
 *     typed years ago, and its label IS itself. Translating it is not possible
 *     and guessing at it would be worse than printing it.
 */
import { builtinForToken, systemForToken } from "@wa/core/payment-methods";
import type { Vars } from "../i18n/message";

/** The provider's own `t`, typed structurally so this file stays React-free. */
export type Translate = (key: string, vars?: Vars) => string;

/**
 * The label for a stored token. `null` and the empty string come back as the
 * caller's `fallback`, which is what a table cell wants and a dropdown does not.
 */
export function paymentMethodLabel(token: string | null | undefined, t: Translate, fallback = "-"): string {
  const raw = (token ?? "").trim();
  if (raw === "") return fallback;
  const builtin = builtinForToken(raw);
  if (builtin) return t(`settings.payments.method.${builtin}`);
  const system = systemForToken(raw);
  if (system) return t(`settings.payments.method.${system}`);
  return raw;
}
